import { type ReactNode, StrictMode, Suspense, lazy } from "react"
import { createRoot } from "react-dom/client"
import { BrowserRouter } from "react-router"

import "./style/style.css"

// TypeScript interfaces and types
declare global {
  interface Window {
    __SENTRY__?: any
    gtag?: (...args: any[]) => void
  }
}

const isEmbed = window.location.pathname.startsWith("/embed/")
const isLanding = window.location.pathname.startsWith("/landing/")

// StrictMode bypass for E2E only. Playwright sets navigator.webdriver=true.
// Removing this trips assistant-ui's MessageRepository "duplicate id"
// invariant on the embed runtime, which unmounts the React tree mid-test.
// Auto-confirm has its own in-flight guard (confirmInFlightRef), so the
// duplicate-send is most likely upstream in chat-runtime initial-message
// hydration. Fix tracked separately; production paths are unaffected
// (navigator.webdriver is false for real users, StrictMode is a no-op
// in prod builds regardless).
const skipStrictMode = typeof navigator !== "undefined" && navigator.webdriver === true
// eslint-disable-next-line react-refresh/only-export-components
const MaybeStrict = ({ children }: { children: ReactNode }) =>
  skipStrictMode ? <>{children}</> : <StrictMode>{children}</StrictMode>

const rootElement = document.getElementById("root")
const root = createRoot(rootElement as HTMLElement)

if (isEmbed) {
  // Minimal provider tree for embed routes — no Sentry, GA, PostHog,
  // GoogleOAuth, or QueryClient. The embed chunk loads only
  // React + Router + chat code.
  const EmbedApp = lazy(() => import("./embed/EmbedApp"))

  root.render(
    <MaybeStrict>
      <BrowserRouter>
        <Suspense fallback={<div />}>
          <EmbedApp />
        </Suspense>
      </BrowserRouter>
    </MaybeStrict>
  )
} else if (isLanding) {
  // Lightweight entry point for ad landing pages — native Sylvia chat
  // with nav bar, no full platform chrome.
  const LandingApp = lazy(() => import("./landing/LandingApp"))

  root.render(
    <StrictMode>
      <BrowserRouter>
        <Suspense fallback={<div />}>
          <LandingApp />
        </Suspense>
      </BrowserRouter>
    </StrictMode>
  )
} else {
  // Full provider stack for the main application.
  // Static imports ensure Vite tree-shakes normally; the `isEmbed` guard
  // prevents these modules from executing on embed routes.
  import("./mainApp").then(({ renderMainApp }) => {
    renderMainApp(root)
  })
}
